import * as React from 'react';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
    Theme,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import { Subscription } from 'apollo-client/util/Observable';
import { FilteredHeaderPage } from '../v2/layouts/FilteredHeaderPage';
import { FeatureLayoutConfigurationList } from './FeatureLayoutConfigurationList';
import {
    FeatureLayoutConfiguration,
    UpdateFeatureLayoutConfigData,
    featureLayoutConfigSubscription,
    DeleteFeatureLayoutConfigData,
    fetchCurrentUserData,
    CurrentUserData,
    fetchScopeFeatureData,
    DropDownData,
    fetchFeatureLayoutConfigurationData,
    FeatureLayoutConfigurationQueryParams,
    FeatureLayoutConfigurationListData,
    RoleCategoryType,
    UpdateFeatureSubscription,
} from './FeatureLayoutConfigurationRepository';
import { DashboardLayoutScopeSelector } from '../components/DashboardLayoutScopeSelector';
import { RoleSelector } from '../components/RoleSelector';
import { RoleCategorySelector } from '../components/RoleCategorySelector';
import { UserAutocompleteSelector } from '../components/UserAutocompleteSelector';
import { PanelSelector } from '../lookupConfiguration/selectors/PanelSelector';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import { LearnMoreEnum } from '../learnMore/LearnMorePage';
import { IAutoCompleteItem } from '../typings/autoComplete';
import { client } from '..';
import { showNotification, RouterProps } from '../App';
import { MatterTerminology } from '../MatterTerminology';

// Matches FeatureLicenceConfigurationList's section-card accent so both configuration pages
// read as one visual language.
const SECTION_BG = 'rgba(81, 115, 255, 0.1)';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        selectorsGroup: {
            display: 'flex',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
            '& .downshift-wrapper': {
                maxWidth: '100%',
            },
            '& .scope-selector': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${theme.palette.primary.main} !important`,
                },
            },
            '& .MuiAutocomplete-popupIndicator': {
                color: theme.palette.primary.main,
            },
            '& .MuiInputLabel-outlined': {
                fontSize: 13,
                transform: 'translate(14px, 13px) scale(1)',
            },
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                transform: 'translate(14px, -6px) scale(0.75)',
            },
            '& .MuiInputBase-input': {
                fontSize: 13,
            },
            '& .MuiFormLabel-root': {
                fontSize: 13,
            },
        },
        selectorField: {
            width: 200,
            flexShrink: 0,
        },
        selectorFieldWide: {
            width: 220,
            flexShrink: 0,
        },
        searchField: {
            width: 220,
            flexShrink: 0,
        },
        actionButton: {
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
        },
        headerButton: {
            textTransform: 'none',
            fontSize: 12,
        },
        sectionCard: {
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
        },
        sectionHead: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
        },
        sectionIcon: {
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: SECTION_BG,
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        sectionTitleRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: 3,
        },
        sectionTitle: {
            fontSize: 12,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: theme.palette.primary.main,
        },
        sectionDescription: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
        },
        countChip: {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '2px 8px',
            borderRadius: 20,
            backgroundColor: SECTION_BG,
            color: theme.palette.primary.main,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.6,
        },
        skeletonGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: theme.spacing(2),
        },
    })
);

export enum TypeOfDashboard {
    firm = 0,
    lead = 1,
    feeEarner = 2,
    task = 3,
    client = 4
}

export interface FeatureLayoutConfigurationProps extends RouterProps {
    // tslint:disable-next-line: no-any
    location: any;
}

interface FeatureLayoutConfigurationState {
    // ── Finalized search/mutation params (previously pushed up from the control panel
    // via onSearchCriteriaChanged) ──
    isInternalRole: boolean | null; // RoleCategory
    isAdministrator: boolean | null; // RoleCategory
    roleId: string | null;
    userEmail: string | null;
    isPersonal: boolean | null;
    featureLayoutConfigurationList: FeatureLayoutConfiguration[];
    filteredFeatureLayoutConfigurationList: FeatureLayoutConfiguration[];
    isSearchChanged: boolean;
    isHideAllOperation: boolean;
    dashboardPanel: IAutoCompleteItem | null;
    isInternalUser: boolean;
    isInternalAdmin: boolean;
    isSaving: boolean;

    // ── Toolbar / selector UI state (previously FeatureLayoutConfigurationControlPanel's
    // own state) ──
    isLoading: boolean;
    isDisableReset: boolean;
    scope: string;
    selectedRoleCategory: string; // RoleCategory
    selectedRoleId: string;
    userLabel: string;
    selectedUserEmail: string;
    isHideAll: boolean;
    showConfirm: boolean;
    search: string;
}

interface InitialPageValues {
    layoutId: number;
    hasBack: boolean;
    personalise: boolean;
    dashboardScope: string | null;
    dashboardPanel: IAutoCompleteItem | null;
}

// Reads (then clears) the values the calling page left in session storage — must run exactly
// once per mount, which is why it's kept behind a ref rather than recomputed each render.
// tslint:disable-next-line: no-any
const readInitialPageValues = (location: any): InitialPageValues => {
    let personaliseStorage = sessionStorage.getItem('personalise');
    let dashboardScope = sessionStorage.getItem('dashboardScope');
    let dashboardPanelValue = sessionStorage.getItem('dashboardPanelValue');
    let dashboardPanelLabel = sessionStorage.getItem('dashboardPanelLabel');

    let layoutId = location.state && location.state.layoutId ? location.state.layoutId : Number(sessionStorage.getItem('layoutId'));

    let dashboardPanel: IAutoCompleteItem | null = { label: 'Home', value: 'all' };

    if (dashboardPanelValue && dashboardPanelLabel) {
        dashboardPanel = { label: dashboardPanelLabel, value: dashboardPanelValue };
    }

    // After assigning the values, clear them from session storage
    sessionStorage.removeItem('layoutId');
    sessionStorage.removeItem('dashboardScope');
    sessionStorage.removeItem('dashboardPanelValue');
    sessionStorage.removeItem('dashboardPanelLabel');

    return {
        layoutId: Number(layoutId),
        hasBack: personaliseStorage !== 'noback',
        personalise: personaliseStorage === 'noback',
        dashboardScope: dashboardScope,
        dashboardPanel: dashboardPanel,
    };
};

const getPageTitle = (layoutId: number): string => {
    if (layoutId === TypeOfDashboard.lead) {
        return MatterTerminology.EnquiryDashboardLayout;
    } else if (layoutId === TypeOfDashboard.feeEarner) {
        return MatterTerminology.FeeEarnerDashboardLayout;
    } else if (layoutId === TypeOfDashboard.task) {
        return MatterTerminology.TaskDashboardLayout;
    } else if (layoutId === TypeOfDashboard.client) {
        return MatterTerminology.ClientDashboardLayout;
    }
    return MatterTerminology.FirmDashboardLayout;
};

export const FeatureLayoutConfigurationPage: React.FC<FeatureLayoutConfigurationProps> = props => {
    const classes = useStyles();
    const history = useHistory();
    const terminology = { ...MatterTerminology };

    const initialValuesRef = React.useRef<InitialPageValues | null>(null);
    if (initialValuesRef.current === null) {
        initialValuesRef.current = readInitialPageValues(props.location);
    }
    const { layoutId, hasBack, personalise, dashboardScope } = initialValuesRef.current;

    // The old page hard-set this to true on every render, so the `=== false` branches below
    // never ran — kept so the query/mutation params stay identical.
    const isShowFilterControls = true as boolean;

    const [state, setFullState] = React.useState<FeatureLayoutConfigurationState>(() => {
        const initialScope = dashboardScope ? dashboardScope : '';
        return {
            isInternalRole: null,
            isAdministrator: null,
            roleId: null,
            userEmail: null,
            isPersonal: dashboardScope === 'DashboardSecurityPersonal' ? true : null,
            featureLayoutConfigurationList: [],
            filteredFeatureLayoutConfigurationList: [],
            isSearchChanged: false,
            isHideAllOperation: false,
            dashboardPanel: initialValuesRef.current ? initialValuesRef.current.dashboardPanel : null,
            isInternalUser: true,
            isInternalAdmin: false,
            isSaving: false,

            isLoading: false,
            isDisableReset: initialScope === 'DashboardSecurityScope' ? true : false,
            scope: initialScope,
            selectedRoleCategory: '',
            selectedRoleId: '',
            userLabel: '',
            selectedUserEmail: '',
            isHideAll: false,
            showConfirm: false,
            search: '',
        };
    });

    // Class-style partial merge, so each call below maps 1:1 onto the old setState calls.
    const setState = React.useCallback((partialState: Partial<FeatureLayoutConfigurationState>) => {
        setFullState(prevState => ({ ...prevState, ...partialState }));
    // tslint:disable-next-line: align
    }, []);

    const isSavePendingRef = React.useRef<boolean>(false);

    // The debounced search and the Apollo subscriptions outlive a single render, so they call
    // through this ref to reach the latest render's handlers (and therefore the latest state) —
    // the same values the old class read off `this.state` at call time.
    // tslint:disable-next-line: no-any
    const latestHandlersRef = React.useRef<any>(null);

    const debouncedSearchRef = React.useRef(debounce((scope: string) => {
        latestHandlersRef.current.handleFeatureLayoutConfigurationSearch(scope);
    // tslint:disable-next-line: align
    }, 500));

    // ── Moved from FeatureLayoutConfigurationPage (class) ─────────────────────────────

    const onFeatureLayoutConfigurationFetch = (
        featureLayoutConfigurationList: FeatureLayoutConfiguration[],
        filteredFeatureLayoutConfigurationList: FeatureLayoutConfiguration[],
        isHideAllOperation: boolean
    ) => {
        setState({
            featureLayoutConfigurationList: featureLayoutConfigurationList,
            filteredFeatureLayoutConfigurationList: filteredFeatureLayoutConfigurationList,
            isSearchChanged: false,
            isHideAllOperation: isHideAllOperation
        });
    };

    const onSearchCriteriaChanged = (
        isInternalRole: boolean | null,
        isAdministrator: boolean | null,
        userEmail: string | null,
        roleId: string | null,
        isPersonal: boolean | null,
        dashboardPanel: IAutoCompleteItem | null
    ) => {
        setState({
            isInternalRole: isInternalRole,
            isAdministrator: isAdministrator,
            userEmail: userEmail,
            roleId: roleId,
            isPersonal: isPersonal,
            dashboardPanel: dashboardPanel,
            isSearchChanged: true,
        });
    };

    const onDragEnd = (featureLayoutConfigurationList: FeatureLayoutConfiguration[]) => {
        setState({
            featureLayoutConfigurationList: featureLayoutConfigurationList,
        });
    };

    // Card visibility is mutated in place on the shared feature objects — this only needs to
    // re-render so the Show All / Hide All label recomputes.
    const onVisibilityChanged = () => {
        setState({});
    };

    const saveFeatureLayoutConfiguration = () => {
        if (state.featureLayoutConfigurationList !== undefined) {

            const featureLayoutConfigurationList = state.featureLayoutConfigurationList;
            const filteredFeatureLayoutConfigurationList = state.filteredFeatureLayoutConfigurationList;

            if (featureLayoutConfigurationList.length !== filteredFeatureLayoutConfigurationList.length) {
                filteredFeatureLayoutConfigurationList.forEach((filtered: FeatureLayoutConfiguration) => {

                    const objIndex = featureLayoutConfigurationList.findIndex((obj => obj.featureGuid === filtered.featureGuid));

                    featureLayoutConfigurationList[objIndex].isHidden = filtered.isHidden;

                });
            }

            // eslint-disable-next-line
            let featureLayoutConfigList = new Array();

            // To save the hidden features at the end of the list.
            const featureList = featureLayoutConfigurationList.filter((item: FeatureLayoutConfiguration) => {
                return !item.isHidden;
            });

            const hiddenFeatureList = featureLayoutConfigurationList.filter((item: FeatureLayoutConfiguration) => {
                return item.isHidden;
            });

            featureList.forEach((featureLayout, index) => {
                featureLayoutConfigList.push(
                    {
                        featureGuid: featureLayout.featureGuid,
                        sequence: index,
                        isHidden: featureLayout.isHidden
                    }
                );
            });

            hiddenFeatureList.forEach((featureLayout, index) => {
                featureLayoutConfigList.push(
                    {
                        featureGuid: featureLayout.featureGuid,
                        sequence: featureList.length + index,
                        isHidden: featureLayout.isHidden
                    }
                );
            });

            client.mutate({
                mutation: UpdateFeatureLayoutConfigData,
                variables: {
                    featureLayoutConfigurationList: featureLayoutConfigList,
                    layoutId: layoutId,
                    isInternalRole: isShowFilterControls === false ? null : state.isInternalRole,
                    isAdministrator: isShowFilterControls === false ? null : state.isAdministrator,
                    roleId: isShowFilterControls === false ? null : state.roleId,
                    userEmail: isShowFilterControls === false ? null : state.userEmail,
                    isPersonal: isShowFilterControls === false ? true : state.isPersonal,
                    dashboardPanelId: state.dashboardPanel ? state.dashboardPanel.value === 'all' ? null : state.dashboardPanel.value : null,
                    // The old control panel never passed a userId up, so this was always null.
                    userId: null
                }
            }) // tslint:disable-next-line:no-any
            .then((results) => {
                if (results.data) {
                    if (results.data.error === null || results.data.error === undefined) {
                        showNotification(null, 'Successfully submitted', 'info');
                    } else {
                        isSavePendingRef.current = false;
                        setState({ isSaving: false });
                        showNotification('Failed to Save Feature Layout Configuration', results.data.error, 'error');
                    }
                } else {
                    isSavePendingRef.current = false;
                    setState({ isSaving: false });
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                isSavePendingRef.current = false;
                setState({ isSaving: false });
                showNotification('Failed to Save Feature Layout Configuration', reason, 'error');
            });
        }
    };

    const deleteFeatureLayoutConfiguration = () => {
        if (state.featureLayoutConfigurationList !== undefined) {
            client.mutate({
                mutation: DeleteFeatureLayoutConfigData,
                variables: {
                    layoutId: layoutId,
                    isInternalRole: isShowFilterControls === false ? null : state.isInternalRole,
                    isAdministrator: isShowFilterControls === false ? null : state.isAdministrator,
                    roleId: isShowFilterControls === false ? null : state.roleId,
                    userEmail: isShowFilterControls === false ? null : state.userEmail,
                    isPersonal: isShowFilterControls === false ? true : state.isPersonal,
                    dashboardPanelId: state.dashboardPanel ? state.dashboardPanel.value === 'all' ? null : state.dashboardPanel.value : null
                }
            }) // tslint:disable-next-line:no-any
            .then((results) => {
                if (results.data) {
                    if (results.data.error === null || results.data.error === undefined) {
                        showNotification(null, 'Successfully submitted', 'info');
                    } else {
                        showNotification('Failed to Reset Feature Layout Configuration', results.data.error, 'error');
                    }
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => { showNotification('Failed to Reset Feature Layout Configuration', reason, 'error'); });
        }
    };

    const handleSave = () => {
        if (isSavePendingRef.current) {
            return;
        }

        isSavePendingRef.current = true;
        setState({ isSaving: true });
        saveFeatureLayoutConfiguration();
    };

    const onRetreivedCurrentUser = (data: CurrentUserData) => {
        setState({
            isInternalUser: data.appUser.isInternal,
            isInternalAdmin: data.appUser.isInternalAdmin
        });
    };

    // ── Moved from FeatureLayoutConfigurationControlPanel ─────────────────────────────

    const fetchScopeData = () => {
        let scopeList = fetchScopeFeatureData(undefined, personalise);

        const scopeData = scopeList.map((scope: DropDownData) => ({ value: scope.value, label: scope.text }));

        if (scopeList != null && scopeList.length > 0) {
            // If there is no value is set, then set the default value to first in scopeData
            const defaultValue = state.scope !== ''
                ? state.scope
                : scopeData[0].value;

            handleChangeFilters(defaultValue, '', 'scopeId');
        }
    };

    const onHideAllChange = () => {
        const featureLayoutConfigurationList = state.filteredFeatureLayoutConfigurationList;
        featureLayoutConfigurationList.forEach(featureLayoutConfig => {
            featureLayoutConfig.isHidden = !state.isHideAll;
        });

        setState({
            isHideAll: !state.isHideAll,
            featureLayoutConfigurationList: featureLayoutConfigurationList,
            filteredFeatureLayoutConfigurationList: featureLayoutConfigurationList,
            isSearchChanged: false,
            isHideAllOperation: true
        });
    };

    // tslint:disable-next-line: no-any
    const onUserSelection = (selection: IAutoCompleteItem | any) => {
        if (selection) {
            onFeatureLayoutConfigurationFetch([], [], false);

            let isInternalRoleParam = null;
            let isAdministratorParam = null;
            let userEmailParam = selection.email;
            let roleIdParam = null;
            let isPersonal = false;
            let dashboardPanel = { label: 'Home', value: 'all' };

            if (layoutId === TypeOfDashboard.client) {
                setState({
                    isDisableReset: (userEmailParam as string).length > 0 ? false : true,
                    selectedUserEmail: userEmailParam,
                    dashboardPanel: dashboardPanel,
                });
            } else {
                setState({
                    selectedUserEmail: userEmailParam,
                    dashboardPanel: dashboardPanel,
                });
            }

            onSearchCriteriaChanged(
                isInternalRoleParam,
                isAdministratorParam,
                userEmailParam,
                roleIdParam,
                isPersonal,
                dashboardPanel,
            );

            debouncedSearchRef.current(state.scope);

        } else {
            setState({
                selectedUserEmail: '',
                isDisableReset: true
            });
        }
    };

    const onPanelSelection = (selection?: IAutoCompleteItem | IAutoCompleteItem[]) => {
        onFeatureLayoutConfigurationFetch([], [], false);

        if (selection && !Array.isArray(selection)) {

            setState({
                dashboardPanel: selection
            });

            let isInternalRoleParam = null;
            let isAdministratorParam = null;
            if (state.scope === 'DashboardSecurityRoleCategory') {
                // tslint:disable-next-line: max-line-length
                isInternalRoleParam = state.selectedRoleCategory !== RoleCategoryType.Administrator.toString() ? state.selectedRoleCategory === '1' : null;
                isAdministratorParam = state.selectedRoleCategory === RoleCategoryType.Administrator.toString() ? true : null;
            }
            let roleIdParam = (state.selectedRoleId !== undefined && state.selectedRoleId.length > 0) ? state.selectedRoleId : null;
            let userEmailParam = (state.selectedUserEmail !== undefined && state.selectedUserEmail.length > 0) ? state.selectedUserEmail : null;
            let isPersonal = (state.scope === 'DashboardSecurityPersonal')
                ? true
                : (state.scope === 'DashboardSecurityUser' ? false : (userEmailParam !== null ? false : null));

            onSearchCriteriaChanged(
                isInternalRoleParam,
                isAdministratorParam,
                userEmailParam,
                roleIdParam,
                isPersonal,
                selection,
            );

            debouncedSearchRef.current(state.scope);
        }
    };

    // tslint:disable-next-line: no-any
    const onSelection = (selection: IAutoCompleteItem | any, name: string) => {
        handleChangeFilters(selection.value, '', name);
    };

    // tslint:disable-next-line: no-any
    const onUserAutocompleteSelection = (selection: IAutoCompleteItem | IAutoCompleteItem[] | any, name: string) => {
        // UserAutocompleteSelector's own item shape carries the user's GUID as `value` and the
        // actual email address as a separate `email` field (unlike the old mui-downshift
        // UserSelector, whose `value` field *was* the email) — the backend query/mutations here
        // key off the email, so that's what has to flow into handleChangeFilters.
        const item = selection || { email: '', label: '' };
        handleChangeFilters(item.email === undefined ? '' : item.email, item.label || '', name);
    };

    // tslint:disable-next-line: no-any
    const handleChangeFilters = (value: any, label: string, name: string) => {
        if (value !== undefined) {
            let isScopeChanged = false;
            let scope = '';
            let isInternalRole = '';
            let userEmail = '';
            let userLabel = '';
            let roleId = '';
            let isDisableReset = true;

            let dashboardPanel: IAutoCompleteItem | null = null;

            if (layoutId === TypeOfDashboard.client) {
                dashboardPanel = state.dashboardPanel;
            } else {
                dashboardPanel = state.dashboardPanel ? state.dashboardPanel : { label: 'Home', value: 'all' };
            }

            if (name === 'scopeId') {

                isScopeChanged = true;
                scope = value;

                if (value === 'DashboardSecurityPersonal') {
                    isDisableReset = false;
                } else {
                    isDisableReset = true;
                }

            } else if (state.scope === 'DashboardSecurityRoleCategory' && name === 'roleCategoryId') {

                isInternalRole = value;

                if (value.toString().length > 0) {
                    isDisableReset = false;
                }

            } else if (state.scope === 'DashboardSecurityRole' && name === 'roleId') {

                roleId = value;

                if (typeof value === 'string' && value.length > 0) {
                    isDisableReset = false;
                }

            } else if (state.scope === 'DashboardSecurityUser' && name === 'userId') {
                userEmail = value;
                userLabel = label;

                if (typeof value === 'string' && value.length > 0) {
                    isDisableReset = false;
                }
            }

            scope = isScopeChanged ? scope : state.scope;

            setState({
                isDisableReset: isDisableReset,
                scope: scope,
                selectedRoleCategory: isInternalRole,
                selectedUserEmail: userEmail,
                userLabel: userLabel,
                selectedRoleId: roleId,
                dashboardPanel: dashboardPanel ? dashboardPanel : state.dashboardPanel
            });

            onFeatureLayoutConfigurationFetch([], [], false);

            if (value === 'DashboardSecurityScope' || value === 'DashboardSecurityPersonal' ||
                (name === 'roleCategoryId' && value !== '') ||
                (name === 'roleId' && value !== '') ||
                (name === 'userId' && value !== '') ||
                (name === 'dashboardPanel' && value !== '')) {
                debouncedSearchRef.current(scope);
            }

            let isInternalRoleParam = null;
            let isAdministratorParam = null;

            if (name === 'roleCategoryId') {
                if (isInternalRole === RoleCategoryType.Administrator.toString()) {
                    isAdministratorParam = true;
                } else {
                    isInternalRoleParam = isInternalRole === '1';
                }
            }

            let roleIdParam = (name === 'roleId' && value !== undefined && value.length > 0) ? value : null;
            let userEmailParam = (name === 'userId' && value !== undefined && value.length > 0) ? value : null;
            let isPersonal = (value === 'DashboardSecurityPersonal')
                ? true
                : (value === 'DashboardSecurityUser' ? false : (userEmailParam !== null ? false : null));

            onSearchCriteriaChanged(
                isInternalRoleParam,
                isAdministratorParam,
                userEmailParam,
                roleIdParam,
                isPersonal,
                dashboardPanel ? dashboardPanel : state.dashboardPanel,
            );
        }
    };

    const handleFeatureLayoutConfigurationSearch = (scope: string) => {

        setState({
            isLoading: true,
            isHideAll: false
        });

        // Clear the records before setting the data again
        onFeatureLayoutConfigurationFetch([], [], false);

        let isAdministrator = null;
        let isInternalRole = null;

        if (state.selectedRoleCategory.length > 0) {
            if (state.selectedRoleCategory === RoleCategoryType.Administrator.toString()) {
                isAdministrator = true;
            } else {
                isInternalRole = state.selectedRoleCategory === '1';
            }
        }

        var queryParams: FeatureLayoutConfigurationQueryParams = {
            includeHidden: true,
            layoutId: layoutId,
            isInternalRole: isShowFilterControls === false ? null : isInternalRole,
            isAdministrator: isShowFilterControls === false ? null : isAdministrator,
            userEmail: isShowFilterControls === false ? null : (state.selectedUserEmail.length > 0 ? state.selectedUserEmail : null),
            roleId: isShowFilterControls === false ? null : (state.selectedRoleId.length > 0 ? state.selectedRoleId : null),
            isPersonal: isShowFilterControls === false
                ? true
                : (scope === 'DashboardSecurityPersonal' ? true : (state.selectedUserEmail.length > 0 ? false : null)),
            dashboardPanelId: state.dashboardPanel ? state.dashboardPanel.value === 'all' ? null : state.dashboardPanel.value : null
        };

        fetchFeatureLayoutConfigurationData(queryParams,
                                            true,
                                            (data: FeatureLayoutConfigurationListData) => {
                                                latestHandlersRef.current.handleFeatureLayoutConfigurationFetch(data);
                                            },
                                            (reason) => {
                                                setState({
                                                    isLoading: false
                                                });

                                                showNotification('Failed to Fetch Layout Configuration', reason, 'error');
                                            });
    };

    const handleFeatureLayoutConfigurationFetch = (data: FeatureLayoutConfigurationListData) => {
        if (data.loading) {
            setState({
                isLoading: true
            });
        }

        if (data && data.featureLayoutConfigurationList !== undefined &&
            data.featureLayoutConfigurationList.featureLayoutConfiguration !== undefined) {

            // This is due to what is being returned from Graphql is a readonly list.
            // This is a intentional in Graphql Apollo. But we need this to be editable.
            // So we make a deep copy.
            const featureLayoutConfigurationList = JSON.parse(JSON.stringify(
                data.featureLayoutConfigurationList.featureLayoutConfiguration));

            let isHideAll = !(data.featureLayoutConfigurationList.featureLayoutConfiguration.filter(x => x.isHidden === false).length > 0);

            let filteredItems: FeatureLayoutConfiguration[] = featureLayoutConfigurationList;

            if (state.search.length > 0) {
                filteredItems = featureLayoutConfigurationList.filter((item: FeatureLayoutConfiguration) => {
                    return item.title.toLowerCase().includes(state.search.toLowerCase().trim());
                });
            }

            setState({
                isLoading: false,
                isHideAll: isHideAll,
                featureLayoutConfigurationList: featureLayoutConfigurationList,
                filteredFeatureLayoutConfigurationList: filteredItems,
                isSearchChanged: false,
                isHideAllOperation: false
            });
        }
    };

    const handleReset = () => {
        setState({
            showConfirm: true
        });
    };

    const handleDeleteConfirm = (isAgreed: boolean) => {
        if (isAgreed) {
            deleteFeatureLayoutConfiguration();
        }

        setState({ showConfirm: false });
    };

    const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const search = event.target.value;
        setState({
            search: search
        });

        debouncedSearchRef.current(state.scope);
    };

    const onClearSearch = () => {
        setState({
            search: ''
        });

        debouncedSearchRef.current(state.scope);
    };

    const onClickBack = () => {
        props.history.goBack();
    };

    const onLearnMore = () => {
        history.push({
            pathname: `learnMore/${LearnMoreEnum[LearnMoreEnum.Configuring_Dashboards]}`,
        });
    };

    const onResources = () => {
        history.push({
            pathname: `/acuityResourceCenter`,
        });
    };

    latestHandlersRef.current = {
        fetchScopeData,
        handleFeatureLayoutConfigurationSearch,
        handleFeatureLayoutConfigurationFetch,
        scope: state.scope,
    };

    React.useEffect(() => {
        latestHandlersRef.current.fetchScopeData();

        fetchCurrentUserData(
            false,
            (data: CurrentUserData) => onRetreivedCurrentUser(data),
            // tslint:disable-next-line: no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
            }
        );

        const featureLayoutConfigurationSubscription: Subscription = client.subscribe({
            query: featureLayoutConfigSubscription
        }).subscribe({
            // tslint:disable-next-line:no-any
            next(results: any) {
                isSavePendingRef.current = false;
                if (results.data.featureLayoutConfigurationNotification.status) {
                    showNotification('Success', results.data.featureLayoutConfigurationNotification.message, 'info');
                    // Fetch again
                    setState({
                        isHideAllOperation: false,
                        isSaving: false
                    });
                    debouncedSearchRef.current(latestHandlersRef.current.scope);
                } else {
                    setState({ isSaving: false });
                    showNotification('Failed', results.data.featureLayoutConfigurationNotification.message, 'error');
                }
            },
            // tslint:disable-next-line:no-any
            error(err: any) {
                isSavePendingRef.current = false;
                setState({ isSaving: false });
                // tslint:disable-next-line:no-console
                console.error(err);
                showNotification(null, err, 'error');
            }
        });

        const featureSubscription: Subscription = client.subscribe({
            query: UpdateFeatureSubscription
        }).subscribe({
            // tslint:disable-next-line:no-any
            next(results: any) {
                if (results.data.featureUpdateNotification.status) {
                    showNotification('Success', results.data.featureUpdateNotification.message, 'info');
                    latestHandlersRef.current.fetchScopeData();
                } else {
                    showNotification('Failed', results.data.featureUpdateNotification.message, 'error');
                }
            },
            // tslint:disable-next-line:no-any
            error(err: any) {
                // tslint:disable-next-line:no-console
                console.error(err);
                showNotification(null, err, 'error');
            }
        });

        const debouncedSearch = debouncedSearchRef.current;

        return () => {
            featureLayoutConfigurationSubscription.unsubscribe();
            featureSubscription.unsubscribe();
            debouncedSearch.cancel();
        };
    // Mount-only, matching the old componentDidMount — later values are read through latestHandlersRef.
    // tslint:disable-next-line: align
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Render ────────────────────────────────────────────────────────────────────────

    // If the list is filtered using the search, then show filtered list
    const isSearch = state.featureLayoutConfigurationList.length !== state.filteredFeatureLayoutConfigurationList.length;
    const displayedFeatureList = isSearch
        ? state.filteredFeatureLayoutConfigurationList
        : state.featureLayoutConfigurationList;

    const isHideAllLabel = !(state.filteredFeatureLayoutConfigurationList.filter(featureLayout => featureLayout.isHidden === false).length > 0);
    const visibleCount = displayedFeatureList.filter(featureLayout => !featureLayout.isHidden).length;

    const isScopeFilterShown = isShowFilterControls === true && layoutId !== TypeOfDashboard.lead && layoutId !== TypeOfDashboard.client;
    const isExternalUserFilterShown = isShowFilterControls === true && layoutId === TypeOfDashboard.client && state.isInternalUser;

    const renderSecondDropDown = (): JSX.Element | null => {
        if (state.scope === 'DashboardSecurityRoleCategory') {
            return (
                <div className={classes.selectorFieldWide}>
                    <RoleCategorySelector
                        label={terminology.RoleCategory}
                        name="roleCategoryId"
                        value={state.selectedRoleCategory}
                        isIncludeAdministrator={true}
                        variant="outlined"
                        size="small"
                        onSelection={onSelection}
                    />
                </div>
            );
        } else if (state.scope === 'DashboardSecurityRole') {
            return (
                <div className={classes.selectorFieldWide}>
                    <RoleSelector
                        label={terminology.Role}
                        name="roleId"
                        value={state.selectedRoleId}
                        variant="outlined"
                        size="small"
                        onSelection={onSelection}
                    />
                </div>
            );
        } else if (state.scope === 'DashboardSecurityUser') {
            return (
                <div className={classes.selectorFieldWide}>
                    <UserAutocompleteSelector
                        label={terminology.User}
                        name="userId"
                        onSelection={onUserAutocompleteSelection}
                        value={state.selectedUserEmail
                            ? { value: state.selectedUserEmail, email: state.selectedUserEmail, label: state.userLabel }
                            : null}
                        isAuthorized={true}
                        variant="outlined"
                        size="small"
                    />
                </div>
            );
        }
        return null;
    };

    return (
        <FilteredHeaderPage
            icon={<TrendingUpIcon fontSize="small" />}
            title={getPageTitle(layoutId)}
            subtitle="Arrange and show or hide the elements displayed on this dashboard"
            onBack={hasBack ? onClickBack : undefined}
            loading={state.isLoading}
            headerActions={(
                <React.Fragment>
                    <Button
                        color="primary"
                        size="small"
                        className={classes.headerButton}
                        onClick={onLearnMore}
                        startIcon={<VideoLibraryOutlinedIcon fontSize="small" />}
                    >
                        Learn More
                    </Button>
                    <Button
                        color="primary"
                        size="small"
                        className={classes.headerButton}
                        onClick={onResources}
                        startIcon={<ErrorOutlineIcon fontSize="small" />}
                    >
                        Resource Centre
                    </Button>
                </React.Fragment>
            )}
            dialogs={(
                <Dialog
                    open={state.showConfirm}
                    onClose={() => handleDeleteConfirm(false)}
                    maxWidth="sm"
                    fullWidth={true}
                >
                    <DialogTitle>Reset Configuration Layout</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure that you want to reset this Configuration Layout?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleDeleteConfirm(false)} color="primary">
                            No
                        </Button>
                        <Button onClick={() => handleDeleteConfirm(true)} color="primary" autoFocus={true}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            filters={(
                <div className={classes.selectorsGroup}>
                    {isScopeFilterShown && (
                        <React.Fragment>
                            <div className={classes.selectorField}>
                                <DashboardLayoutScopeSelector
                                    label={terminology.Scope}
                                    name="scopeId"
                                    value={state.scope}
                                    className="scope-selector"
                                    variant="outlined"
                                    size="small"
                                    onSelection={onSelection}
                                />
                            </div>
                            {renderSecondDropDown()}
                        </React.Fragment>
                    )}
                    {isExternalUserFilterShown && (
                        <div className={classes.selectorFieldWide}>
                            <UserAutocompleteSelector
                                label="User"
                                name="externalUser"
                                isExternal={true}
                                onSelection={onUserSelection}
                                variant="outlined"
                                size="small"
                            />
                        </div>
                    )}
                    <div className={classes.selectorField}>
                        <PanelSelector
                            label="Panel"
                            name="dashboardPanel"
                            value={state.dashboardPanel}
                            onSelection={onPanelSelection}
                            dashboardPanelUsedBy={layoutId}
                            variant="outlined"
                            size="small"
                        />
                    </div>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search..."
                        className={classes.searchField}
                        value={state.search}
                        onChange={onSearchChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {state.search.length <= 0
                                        ? <SearchOutlinedIcon fontSize="small" color="action" />
                                        : (
                                            <IconButton onClick={onClearSearch} edge="end" size="small" aria-label="Clear search">
                                                <CloseOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
            )}
            actions={(
                <React.Fragment>
                    <Tooltip
                        title={state.search.length !== 0 ? 'Clear the search to show or hide all elements.' : ''}
                        disableHoverListener={state.search.length === 0}
                    >
                        <span>
                            <Button
                                size="small"
                                className={classes.actionButton}
                                onClick={onHideAllChange}
                                disabled={state.search.length !== 0}
                                startIcon={isHideAllLabel
                                    ? <VisibilityOutlinedIcon fontSize="small" />
                                    : <VisibilityOffOutlinedIcon fontSize="small" />}
                            >
                                {isHideAllLabel ? 'Show All' : 'Hide All'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        onClick={handleSave}
                        disabled={state.isSaving}
                        className={classes.actionButton}
                        startIcon={<SaveOutlinedIcon style={{ fontSize: 16 }} />}
                    >
                        {state.isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Tooltip
                        title={state.isDisableReset
                            ? 'Select a role category, role, or user to enable Reset.'
                            : ''}
                        disableHoverListener={!state.isDisableReset}
                    >
                        <span>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                color="secondary"
                                className={classes.actionButton}
                                disabled={state.isDisableReset}
                                startIcon={<SettingsBackupRestoreIcon style={{ fontSize: 16 }} />}
                            >
                                Reset
                            </Button>
                        </span>
                    </Tooltip>
                </React.Fragment>
            )}
        >
            <Paper className={classes.sectionCard}>
                <div className={classes.sectionHead}>
                    <div className={classes.sectionIcon}>
                        <DashboardOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                        <div className={classes.sectionTitleRow}>
                            <Typography className={classes.sectionTitle}>
                                Dashboard Elements
                            </Typography>
                            {displayedFeatureList.length > 0 && (
                                <span className={classes.countChip}>
                                    {visibleCount} of {displayedFeatureList.length} visible
                                </span>
                            )}
                        </div>
                        <Typography className={classes.sectionDescription}>
                            {isSearch
                                ? 'Clear the search to drag elements into a new order.'
                                : 'Drag elements to reorder them, and use the toggle to show or hide each one.'}
                        </Typography>
                    </div>
                </div>

                {state.isLoading && displayedFeatureList.length === 0 && (
                    <div className={classes.skeletonGrid}>
                        {Array.from({ length: 8 }).map((_, skeletonIndex) => (
                            <SkeletonLoader key={skeletonIndex} variant="card" rows={1} />
                        ))}
                    </div>
                )}

                <FeatureLayoutConfigurationList
                    featureLayoutConfigurationList={displayedFeatureList}
                    isSearch={isSearch}
                    onDragEnd={onDragEnd}
                    isDragDisabled={isSearch}
                    isHideAllOperation={state.isHideAllOperation}
                    isSearchChanged={state.isSearchChanged}
                    onVisibilityChanged={onVisibilityChanged}
                    isInternalAdmin={state.isInternalAdmin}
                />
            </Paper>
        </FilteredHeaderPage>
    );
};
